import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CalendarCheck,
  CalendarClock,
  CheckCheck,
  ClipboardCheck,
  Megaphone,
  UserCheck,
} from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { announceNotificationLinks } from '@/hooks/useRefreshKey';
import { cn } from '@/lib/utils';
import type { AppNotification, NotificationType } from '@/types/notification';
import { notificationsApi, type NotificationSource } from '../api/notifications.api';
import { timeAgo } from '../utils';

/**
 * The bell, shared by both consoles.
 *
 * One component rather than one per console: the institute area and the teacher
 * dashboard show the same inbox, and two copies would be two behaviours the
 * first time "mark as read" changes (CLAUDE.md §3).
 *
 * Deliberately not a live socket. The count refreshes when the panel is opened,
 * when the tab comes back into view, and on a short interval while it is in
 * view — so a leave request a teacher has just sent reaches the office within
 * seconds, without a connection held open per signed-in account.
 */

/**
 * How long between quiet refreshes while the tab is in view. A minute left an
 * office looking at a stale count while a teacher waited on a leave request.
 */
const REFRESH_MS = 20_000;

const ICON: Record<NotificationType, typeof Bell> = {
  STAFF_ATTENDANCE_PUNCHED_IN: ClipboardCheck,
  STAFF_ATTENDANCE_REVIEWED: UserCheck,
  STAFF_LEAVE_DECIDED: CalendarCheck,
  STAFF_LEAVE_APPLIED: CalendarClock,
  STUDENT_LEAVE_APPLIED: CalendarClock,
  CLASS_NOTICE_POSTED: Megaphone,
  STUDENT_LEAVE_DECIDED: CalendarCheck,
};

export function NotificationBell({
  className,
  source = notificationsApi,
}: {
  className?: string;
  /** Whose inbox — the staff one unless a console passes its own. */
  source?: NotificationSource;
}) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  /**
   * Read here but not yet confirmed by a fresh inbox. Without it the badge kept
   * its old number after a click: the refresh raced the "mark read" request and
   * usually won, so the server still counted the notification as unread.
   */
  const [readHere, setReadHere] = useState<ReadonlySet<string>>(() => new Set());
  const panelRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useApiQuery(() => source.inbox(), [reloadKey]);

  /**
   * Every notification this bell has already shown. Null until the first inbox
   * arrives, which is taken as the starting point: what was waiting at sign-in
   * is not news to the page underneath, and announcing it would reload it.
   */
  const seen = useRef<Set<string> | null>(null);

  // Tells the page under the bell when something new points at it, so a list
  // the badge has just counted into is read again (`useRefreshKey`).
  useEffect(() => {
    if (!data) return;
    if (seen.current === null) {
      seen.current = new Set(data.notifications.map((notification) => notification.uuid));
      return;
    }

    const known = seen.current;
    const arrived = data.notifications.filter((notification) => !known.has(notification.uuid));
    arrived.forEach((notification) => known.add(notification.uuid));
    announceNotificationLinks(
      arrived.flatMap((notification) => (notification.link ? [notification.link] : [])),
    );
  }, [data]);

  // A quiet poll while the tab is in view, and an immediate one when it comes
  // back — the office switching to this tab is when the count has to be right.
  // A hidden tab stops asking. Cleared on unmount so a signed-out session stops.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') setReloadKey((key) => key + 1);
    };

    const timer = setInterval(refresh, REFRESH_MS);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  // Click outside and Escape both close it — a panel that can only be dismissed
  // by the button that opened it is a trap on a phone.
  useEffect(() => {
    if (!isOpen) return;

    function onPointerDown(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) setIsOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  // Only what the inbox still calls unread is taken off, so once a refresh
  // confirms the read the same notification is not subtracted twice.
  const notifications = (data?.notifications ?? []).map((notification) =>
    readHere.has(notification.uuid) ? { ...notification, isRead: true } : notification,
  );
  const readButNotConfirmed = (data?.notifications ?? []).filter(
    (notification) => !notification.isRead && readHere.has(notification.uuid),
  ).length;
  const unread = Math.max(0, (data?.unreadCount ?? 0) - readButNotConfirmed);
  // The skeleton is for the first load only. A refresh keeps the list on
  // screen, or every poll would blank an open panel.
  const isFirstLoad = isLoading && !data;

  function markReadHere(uuids: string[]) {
    setReadHere((current) => new Set([...current, ...uuids]));
  }

  function unmarkReadHere(uuids: string[]) {
    setReadHere((current) => new Set([...current].filter((uuid) => !uuids.includes(uuid))));
  }

  function open() {
    // Opening is the moment the count is worth re-checking.
    if (!isOpen) setReloadKey((key) => key + 1);
    setIsOpen((wasOpen) => !wasOpen);
  }

  async function pick(notification: AppNotification) {
    setIsOpen(false);

    // Read state is a convenience, not a fact anybody depends on — so the
    // reader goes where they were going at once, and the badge drops at once.
    if (notification.link) navigate(notification.link);
    if (notification.isRead) return;

    markReadHere([notification.uuid]);
    try {
      await source.markRead(notification.uuid);
    } catch {
      // Not saved, so it is unread again rather than quietly wrong.
      unmarkReadHere([notification.uuid]);
    }
    // Only now: a refresh sent before the write lands reads the old count.
    setReloadKey((key) => key + 1);
  }

  async function clearAll() {
    const unreadUuids = notifications
      .filter((notification) => !notification.isRead)
      .map((notification) => notification.uuid);
    markReadHere(unreadUuids);
    try {
      await source.markAllRead();
    } catch {
      unmarkReadHere(unreadUuids);
    }
    setReloadKey((key) => key + 1);
  }

  return (
    <div ref={panelRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={open}
        aria-label={unread === 0 ? 'Notifications' : `Notifications — ${unread} unread`}
        aria-expanded={isOpen}
        className="relative grid h-9 w-9 place-items-center rounded-field text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <Bell className="h-[18px] w-[18px]" strokeWidth={1.8} />
        {unread > 0 && (
          <span
            className="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-pill bg-crit px-1 text-2xs font-[650] text-white"
            aria-hidden
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 z-50 mt-2 flex max-h-[26rem] w-[min(22rem,calc(100vw-2rem))] flex-col rounded-card border border-line bg-surface shadow-card"
        >
          <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
            <h2 className="text-base">Notifications</h2>
            {unread > 0 && (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<CheckCheck className="h-3.5 w-3.5" />}
                onClick={() => void clearAll()}
              >
                Mark all read
              </Button>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isFirstLoad && (
              <div className="p-4">
                <SkeletonLines count={4} />
              </div>
            )}

            {!isFirstLoad && error && !data && (
              <p className="px-4 py-8 text-center text-sm text-ink-2">{error.message}</p>
            )}

            {data && notifications.length === 0 && (
              <p className="px-4 py-10 text-center text-sm text-ink-3">
                Nothing yet. You will be told here when something needs you.
              </p>
            )}

            {data && notifications.length > 0 && (
              <ul className="flex flex-col divide-y divide-line">
                {notifications.map((notification) => {
                  const Icon = ICON[notification.type] ?? Bell;

                  return (
                    <li key={notification.uuid}>
                      <button
                        type="button"
                        onClick={() => void pick(notification)}
                        className={cn(
                          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-2',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                          !notification.isRead && 'bg-accent-soft/40',
                        )}
                      >
                        <span
                          className={cn(
                            'mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full',
                            notification.isRead
                              ? 'bg-surface-2 text-ink-3'
                              : 'bg-accent-soft text-accent',
                          )}
                          aria-hidden
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </span>

                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline justify-between gap-2">
                            <span
                              className={cn(
                                'truncate text-sm text-ink',
                                !notification.isRead && 'font-[650]',
                              )}
                            >
                              {notification.title}
                            </span>
                            <span className="shrink-0 text-2xs text-ink-3">
                              {timeAgo(notification.createdAt)}
                            </span>
                          </span>
                          <span className="mt-0.5 block text-xs text-ink-2">
                            {notification.body}
                          </span>
                        </span>

                        {!notification.isRead && <span className="sr-only">Unread</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
